import React, { useContext } from 'react'
import { Navigate, UNSAFE_NavigationContext } from 'react-router-dom'

/**
 * SSR-safe Navigate. React Router's <Navigate> fires its navigation in
 * useLayoutEffect which never runs on the server. And useNavigate()
 * guards against calls during render (returns early if not in an effect).
 *
 * This wrapper bypasses both by accessing the navigator directly via
 * UNSAFE_NavigationContext and calling push/replace during render,
 * which lets ssrNavigator in the web-worker capture the redirect
 * and return a 302.
 *
 * On the client it falls through to the standard <Navigate>.
 *
 * Uses __SERVER__ (set by webpack DefinePlugin) instead of
 * `typeof window === 'undefined'` because the web-worker bundle
 * polyfills window via window.polyfill.js.
 */
export const SSRNavigate = ({
  to,
  replace,
  state,
}: {
  to: string
  replace?: boolean
  state?: any
}) => {
  const { navigator } = useContext(UNSAFE_NavigationContext)

  if (typeof __SERVER__ !== 'undefined' && __SERVER__) {
    if (replace) {
      navigator.replace(to, state)
    } else {
      navigator.push(to, state)
    }
    return null
  }

  return <Navigate to={to} replace={replace} state={state} />
}
